import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import DynamicIcon from '../components/DynamicIcon';
import modullerData from '../default_datas/moduller/modullerData';

const ModulPage = () => {
  const { modulId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();

  const modul = modullerData[modulId];

  const t = (obj, field) => {
    if (!obj) return '';
    return lang === 'en' ? (obj[`${field}_en`] || obj[field] || '') : (obj[field] || '');
  };

  if (!modul) {
    return (
      <div className="min-h-screen bg-[#f5faf9] flex items-center justify-center font-poppins">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#0D3A2F] mb-4">
            {lang === 'en' ? 'Module Not Found' : 'Modül Bulunamadı'}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="bg-[#2a9d8f] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#238a7e] transition-colors"
          >
            {lang === 'en' ? 'Go Home' : 'Ana Sayfaya Dön'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={`${t(modul, 'title')} | RUZY`}
        description={t(modul, 'description')}
        path={`/moduller/${modulId}`}
      />

      <div className="min-h-screen bg-[#f5faf9] font-poppins text-[#0D3A2F]">
        <ScrollReveal>
          <section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0D3A2F] via-[#15614F] to-[#2a9d8f]" />
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#2a9d8f] rounded-full blur-3xl" />
            </div>
            
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-28">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white/90 text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-2 rounded-full mb-4 sm:mb-6 tracking-wider">
                    <DynamicIcon name={modul.icon} size={14} />
                    {t(modul, 'badge')}
                  </div>
                  
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                    {t(modul, 'title')}
                  </h1>
                  
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/80 mb-6 sm:mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                    {t(modul, 'description')}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
                    <button
                      onClick={() => navigate('/iletisim')}
                      className="bg-white text-[#0D3A2F] px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-white/90 transition-all text-sm sm:text-base"
                    >
                      {lang === 'en' ? 'Request Demo' : 'Demo Talep Et'}
                    </button>
                    <button
                      onClick={() => navigate('/fiyatlandirma')}
                      className="bg-transparent border-2 border-white/50 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-white/10 transition-all text-sm sm:text-base"
                    >
                      {lang === 'en' ? 'View Pricing' : 'Fiyatları Gör'}
                    </button>
                  </div>
                </div>

                <div className="relative mt-8 lg:mt-0">
                  <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-xl hidden sm:block" />
                  <img
                    src={Array.isArray(modul.image) ? modul.image[0]?.url : modul.image}
                    alt={t(modul, 'title')}
                    className="relative w-full rounded-xl sm:rounded-2xl shadow-2xl object-cover aspect-video"
                    onError={(e) => {
                      e.target.src = '/images/placeholder.png'; 
                    }}
                  />

                  <div className="hidden sm:block absolute -bottom-4 sm:-bottom-6 left-2 sm:-left-6 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 shadow-xl">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#e0f2ef] rounded-lg sm:rounded-xl flex items-center justify-center">
                        <DynamicIcon name={modul.icon} size={20} className="text-[#2a9d8f]" />
                      </div>
                      <div>
                        <div className="text-[10px] sm:text-xs text-[#6b9e96] font-medium">ISO 27001</div>
                        <div className="text-sm sm:text-lg font-bold text-[#0D3A2F]">
                          {lang === 'en' ? 'Compliant' : 'Uyumlu'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
              {modul.stats?.map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-[#e0eeeb] hover:shadow-lg transition-shadow"
                >
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#2a9d8f] mb-1 sm:mb-2">
                    {stat.value}
                  </div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-[#4a6b63] font-medium">
                    {t(stat, 'label')}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-16">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-3 sm:mb-4">
                {lang === 'en' ? 'Key Features' : 'Temel Özellikler'}
              </h2>
              <p className="text-xs sm:text-sm md:text-base text-[#4a6b63] max-w-2xl mx-auto px-4">
                {lang === 'en' 
                  ? 'Everything you need to manage your information security effectively'
                  : 'Bilgi güvenliğinizi etkin şekilde yönetmek için ihtiyacınız olan her şey'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {modul.features?.map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-[#e0eeeb] hover:shadow-xl hover:border-[#2a9d8f]/30 transition-all group"
                >
                  <div className="w-10 h-10 sm:w-12 md:w-14 sm:h-12 md:h-14 bg-[#e0f2ef] rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-5 group-hover:bg-[#2a9d8f] transition-colors">
                    <span className="text-[#2a9d8f] group-hover:text-white transition-colors">
                      <DynamicIcon name={feature.icon} size={22} />
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-[#0D3A2F] mb-2 sm:mb-3">
                    {t(feature, 'title')}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#4a6b63] leading-relaxed">
                    {t(feature, 'description')}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </ScrollReveal>

        {modul.assetTypes && (
          <ScrollReveal delay={0.3}>
            <section className="bg-white py-10 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-4">
                    {lang === 'en' ? 'Supported Asset Types' : 'Desteklenen Varlık Türleri'}
                  </h2>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-2 sm:gap-4">
                  {modul.assetTypes.map((asset, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-lg sm:rounded-xl bg-[#f5faf9] hover:bg-[#e0f2ef] transition-colors"
                    >
                      <div className="w-8 h-8 sm:w-10 md:w-12 sm:h-10 md:h-12 bg-white rounded-lg sm:rounded-xl flex items-center justify-center shadow-sm">
                        <span className="text-[#2a9d8f]">
                          <DynamicIcon name={asset.icon} size={18} />
                        </span>
                      </div>
                      <span className="text-[9px] sm:text-[10px] md:text-xs font-medium text-[#0D3A2F] text-center leading-tight">
                        {t(asset, 'name')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {modul.roles && (
          <ScrollReveal delay={0.3}>
            <section className="bg-white py-10 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-4">
                    {lang === 'en' ? 'User Roles' : 'Kullanıcı Rolleri'}
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                  {modul.roles.map((role, idx) => (
                    <div
                      key={idx}
                      className="bg-[#f5faf9] rounded-lg sm:rounded-xl p-3 sm:p-5 text-center hover:shadow-md transition-shadow"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#2a9d8f] rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                        <span className="text-white font-bold text-sm sm:text-lg">
                          {t(role, 'name').charAt(0)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#0D3A2F] text-xs sm:text-sm mb-1">
                        {t(role, 'name')}
                      </h3>
                      <p className="text-[10px] sm:text-xs text-[#4a6b63] leading-tight">
                        {t(role, 'desc')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {modul.controlCategories && (
          <ScrollReveal delay={0.3}>
            <section className="bg-white py-10 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-4">
                    ISO 27001:2022 Ek A
                  </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                  {modul.controlCategories.map((cat, idx) => (
                    <div
                      key={idx}
                      className="bg-gradient-to-br from-[#0D3A2F] to-[#15614F] rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white"
                    >
                      <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">{cat.code}</div>
                      <div className="text-sm sm:text-base md:text-lg font-semibold mb-1">{t(cat, 'name')}</div>
                      <div className="text-white/70 text-xs sm:text-sm">
                        {cat.count} {lang === 'en' ? 'Controls' : 'Kontrol'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {modul.auditTypes && (
          <ScrollReveal delay={0.3}>
            <section className="bg-white py-10 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-4">
                    {lang === 'en' ? 'Audit Types' : 'Denetim Türleri'}
                  </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                  {modul.auditTypes.map((audit, idx) => (
                    <div
                      key={idx}
                      className="bg-[#f5faf9] rounded-lg sm:rounded-xl p-4 sm:p-6 border border-[#e0eeeb]"
                    >
                      <h3 className="font-bold text-[#0D3A2F] text-sm sm:text-base mb-1 sm:mb-2">{t(audit, 'name')}</h3>
                      <p className="text-xs sm:text-sm text-[#4a6b63] leading-relaxed">{t(audit, 'desc')}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {modul.trainingTypes && (
          <ScrollReveal delay={0.3}>
            <section className="bg-white py-10 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-4">
                    {lang === 'en' ? 'Training Types' : 'Eğitim Türleri'}
                  </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                  {modul.trainingTypes.map((training, idx) => (
                    <div
                      key={idx}
                      className="bg-[#f5faf9] rounded-lg sm:rounded-xl p-4 sm:p-6 text-center hover:shadow-md transition-shadow"
                    >
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#e0f2ef] rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                        <span className="text-[#2a9d8f]">
                          <DynamicIcon name={training.icon} size={24} />
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#0D3A2F] text-xs sm:text-sm md:text-base">{t(training, 'name')}</h3>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {modul.actionTypes && (
          <ScrollReveal delay={0.3}>
            <section className="bg-white py-10 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-4">
                    {lang === 'en' ? 'Tracked Actions' : 'Takip Edilen Eylemler'}
                  </h2>
                </div>
                <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
                  {modul.actionTypes.map((action, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 sm:gap-3 bg-[#f5faf9] rounded-full px-4 sm:px-6 py-2 sm:py-3"
                    >
                      <span className="text-[#2a9d8f]">
                        <DynamicIcon name={action.icon} size={16} />
                      </span>
                      <span className="font-medium text-[#0D3A2F] text-xs sm:text-sm md:text-base">{t(action, 'name')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        {modul.exportFormats && (
          <ScrollReveal delay={0.3}>
            <section className="bg-white py-10 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                <div className="text-center mb-8 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#0D3A2F] mb-4">
                    {lang === 'en' ? 'Export Formats' : 'Dışa Aktarma Formatları'}
                  </h2>
                </div>
                <div className="flex flex-wrap justify-center gap-4 sm:gap-8">
                  {modul.exportFormats.map((format, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col items-center gap-2 sm:gap-3 bg-[#f5faf9] rounded-xl sm:rounded-2xl px-6 sm:px-10 py-4 sm:py-6"
                    >
                      <span className="text-[#2a9d8f]">
                        <DynamicIcon name={format.icon} size={28} />
                      </span>
                      <span className="font-bold text-[#0D3A2F] text-sm sm:text-base">{format.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>
        )}

        <ScrollReveal delay={0.4}>
          <section className="bg-gradient-to-r from-[#0D3A2F] to-[#2a9d8f] py-10 sm:py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
                {lang === 'en' ? 'Ready to Get Started?' : 'Başlamaya Hazır mısınız?'}
              </h2>
              <p className="text-white/80 mb-6 sm:mb-8 text-sm sm:text-base md:text-lg max-w-xl mx-auto">
                {lang === 'en'
                  ? 'Contact us today for a personalized demo of RUZY'
                  : 'RUZY\'nin kişiselleştirilmiş demosu için bugün bizimle iletişime geçin'}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
                <button
                  onClick={() => navigate('/iletisim')}
                  className="bg-white text-[#0D3A2F] px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-white/90 transition-all text-sm sm:text-base"
                >
                  {lang === 'en' ? 'Contact Us' : 'İletişime Geç'}
                </button>
                <button
                  onClick={() => navigate('/fiyatlandirma')}
                  className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-white/10 transition-all text-sm sm:text-base"
                >
                  {lang === 'en' ? 'View Pricing' : 'Fiyatları Gör'}
                </button>
              </div>
            </div>
          </section>
        </ScrollReveal>
      </div>
    </>
  );
};

export default ModulPage;
